import { CustomAnalyticsEvent, ExternalPixelEvent } from '@betterfunnel/types';
import { DocumentData, FirestoreDataConverter } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { RefObject } from 'react';
import seedrandom from 'seedrandom';
import { twMerge } from 'tailwind-merge';
import { storage } from '../Firebase';
import { AnalyticsRange } from '../contexts/DashboardContext';

// makes sure a function only invoes once,
export function concurrentSafeExecution<
  T extends (...args: any[]) => Promise<any>,
>(asyncFn: T): T {
  let promise: Promise<T> | null = null;

  const execute = async (...args: any[]): Promise<T> => {
    if (!promise) {
      const _promise = asyncFn(...args);
      promise = _promise;
      if (_promise?.finally) {
        _promise.finally(() => (promise = null));
      } else {
        promise = null;
      }

      return _promise;
    }
    return promise;
  };

  return execute as any;
}

export function distinct<T>(items: T[], getValue?: (item: T) => string): T[] {
  const distinctItems: T[] = [];
  const uniqueValues: Set<string> = new Set();

  const _getValue = getValue || ((i) => `${i}`);
  for (const item of items) {
    const value = _getValue(item);

    if (!uniqueValues.has(value)) {
      uniqueValues.add(value);
      distinctItems.push(item);
    }
  }

  return distinctItems;
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let result = '';

  if (hours > 0) {
    result += `${hours}h `;
  }

  if (minutes > 0) {
    result += `${minutes}m `;
  }

  if (remainingSeconds > 0 || result === '') {
    result += `${remainingSeconds}s`;
  }

  return result.trim();
}

export const emptyConverter = <
  T extends DocumentData,
>(): FirestoreDataConverter<T, T> => {
  return {
    toFirestore: (v) => v as any,
    fromFirestore: (v) => v.data() as any,
  };
};

export function chunk<T>(arr: T[], chunkSize: number): T[][] {
  const chunkedArray = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunkedArray.push(arr.slice(i, i + chunkSize));
  }
  return chunkedArray;
}

export function classNames(...classes: any[]) {
  return twMerge(...classes.filter(Boolean));
}

export async function uploadFile(file: File, path: string) {
  const res = await uploadBytes(ref(storage, path), file);

  return getDownloadURL(res.ref);
}

// export const PREVIEW_ROOT = 'https://preview_.betterfunnel.page';
// export const PREVIEW_ROOT = 'http://preview_.localhost:4000';
export const PREVIEW_ROOT = 'https://betterfunnel.site';

export function buildPreviewUrl(
  domain: string,
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  token: string,
  editable: boolean,
) {
  return `${PREVIEW_ROOT}?site=${domain}&websiteId=${websiteId}&accountId=${accountId}&campaignId=${dynamicPageId}&token=${token}${
    editable ? `&editable=${editable}` : ''
  }`;
}

export const STEPPER_PREVIEW_URL = `${PREVIEW_ROOT}/stepper`;

export function sendMessageToIframe(
  message: any,
  ref?: RefObject<HTMLIFrameElement>,
) {
  const cleanMessage = JSON.stringify(message);

  if (ref?.current) {
    ref.current.contentWindow?.postMessage(cleanMessage, PREVIEW_ROOT);
    return true;
  }

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

type KeyFunction<T> = (item: T) => string;

export function groupBy<T>(
  items: T[],
  keyFn: KeyFunction<T>,
): { [key: string]: T[] } {
  const groups: { [key: string]: T[] } = {};

  for (const item of items) {
    const key = keyFn(item);

    if (groups[key]) {
      groups[key].push(item);
    } else {
      groups[key] = [item];
    }
  }

  return groups;
}

export function arrayRandomElements<T>(array: T[], count: number): T[] {
  if (!array || !array.length) return [];

  const maxCount = Math.min(count, array.length);
  if (maxCount === array.length) return array;

  const rndElements = new Set<T>();
  let looper = 0;
  while (looper <= 1000 && rndElements.size < maxCount) {
    looper++;
    const indx = Math.floor(Math.random() * (array.length - 1));
    const newEleme = array[indx];
    if (!rndElements.has(newEleme)) {
      rndElements.add(newEleme);
    }
  }

  return Array.from(rndElements);
}

export function arrayRandomElement<T>(array: T[]): T | undefined {
  return arrayRandomElements(array, 1)[0];
}

export function getRandomElementWithSeed<T>(
  seed: string,
  array: T[],
): T | undefined {
  // Create a seeded random number generator.
  const rng = seedrandom(seed);

  // Generate a random index based on the array length.
  const randomIndex = Math.floor(rng() * array.length);

  // Return the element at the random index.
  return array[randomIndex];
}

export function camelCaseToTitle(camelCase: string) {
  // Use a regular expression to split the camelCase string
  // and add spaces between words
  return camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function capitalize(input: string): string {
  if (!input) return input;
  return input.charAt(0).toUpperCase() + input.slice(1);
}

export type WithId<T> = { id: string } & T;

function dateToString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function analyticsRangeToDates(analyticsRange: AnalyticsRange): {
  startDate: string;
  endDate: string;
} {
  if (!analyticsRange) {
    throw new Error('analyticsRange is required');
  }

  if (typeof analyticsRange === 'string') {
    switch (analyticsRange) {
      case 'today': {
        const startDate = new Date();
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'yesterday': {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);

        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last7Days': {
        const endDate = new Date();
        const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last30Days': {
        const endDate = new Date();
        const startDate = new Date(
          endDate.getTime() - 30 * 24 * 60 * 60 * 1000,
        );
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'currentMonth': {
        const startDate = new Date();
        startDate.setDate(1);
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      default:
        throw new Error('unsupported date range');
    }
  }

  const startDate = new Date(analyticsRange.startDate || '');
  const endDate = new Date(analyticsRange.endDate || '');

  return {
    startDate: dateToString(startDate),
    endDate: dateToString(endDate),
  };
}

export function isJwtExpired(token: string): boolean {
  if (!token) return true;

  // Split the token into its parts
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) return true; // Invalid token format

  try {
    // Decode the payload (second part of the token)
    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const { exp } = JSON.parse(jsonPayload);

    // Check if the token has expired
    if (!exp) return true; // No exp claim in token
    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    return now >= exp; // Token is expired if current time is greater than or equal to exp
  } catch (_) {
    return true; // Error decoding token
  }
}

export type AnalyticsAttributes = {
  visitors: number;
  conversions: number;
  allConversions: number;
  buttonClicks: number;
  uniqueButtonClicks: number;
  formSubmissions: number;
  cr: number;
};

export type AnalyticsAttributesWithCustomEvents = AnalyticsAttributes & {
  [key: string]: number;
};

export function getAnalyticsAttributes(
  key: string,
  analyticsObj?: Record<string, any>,
): AnalyticsAttributes {
  const visitors = analyticsObj?.uniqueVisitors?.[key] ?? 0;
  const conversions = analyticsObj?.uniqueConversions?.[key] ?? 0;
  const allConversions = analyticsObj?.uniqueConversions?.['totalCustom'] ?? 0;
  const buttonClicks = analyticsObj?.uniqueButtonClicks?.[key] ?? 0;
  const uniqueButtonClicks =
    analyticsObj?.uniqueButtonClicks?.['uniqueCustom'] ?? 0;
  const formSubmissions = analyticsObj?.uniqueFormSubmissions?.[key] ?? 0;
  const cr = visitors ? Math.ceil((conversions / visitors) * 100) : 0;

  return {
    visitors,
    conversions,
    allConversions,
    buttonClicks,
    uniqueButtonClicks,
    formSubmissions,
    cr,
  };
}

export function getAnalyticsAttributesWithEvents(
  key: string,
  analyticsObj?: Record<string, any>,
  customEvents?: (ExternalPixelEvent | CustomAnalyticsEvent)[],
): AnalyticsAttributesWithCustomEvents {
  const attrs = getAnalyticsAttributes(key, analyticsObj);

  return Object.assign(
    {},
    attrs,
    customEvents?.reduce(
      (acc, event) => {
        acc[event.id] = analyticsObj?.[event.id]?.[key] ?? 0;
        return acc;
      },
      {} as Record<string, number>,
    ),
  );
}
